import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Checkbox, Drawer, Form, Select, Tabs, Tooltip } from 'antd';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { CheckCircle, ChevronDown, ChevronLeft, ChevronUp, CloseIcon, Information } from '../../assets/icons';
import FloatingLabel from '../../components/FloatingLabel/FloatingLabel';
import ListingAgreement from '../../components/ListingAgreement/ListingAgreement';
import PinDrawerWithoutButton from '../../components/PinDrawerWithoutButton/PinDrawerWithoutButton';
import BuyOrder from '../../components/SecondaryTrade/BuyOrder';
import SellOrder from '../../components/SecondaryTrade/SellOrder';
import { Images } from '../../config/images';
import { formatePhoneNumber } from '../../utils/formatPhoneNumber';
import { getBankLogo } from '../../utils/getBankLogo';
import { getUsCurrencyFormat, getUsCurrencyFormatWithoutDecimal } from '../../utils/usCurrencyFormatter';
import ResetPin from '../Account/Profile/Modals/ResetPin';
import { BuyReviewData, BuySuccessData, SellReviewData, SellSuccessData } from './TradeData';
import BankAccountModal from '../../components/LinkBankAccount/BankAccountModal';
import { secondaryBuy } from '../../constants/secondaryBuy';
import { BankVerificationPendingModal } from '../../components/Instances/BankVerificationPendingModal';
import { NoFundModal } from '../../components/Instances/NoFundModal';
import { PendingTransferModal } from '../../components/Instances/PendingTransferModal';
import TransferMoreFunds from '../../components/Instances/TransferMoreFunds';
import SecondaryMarketAccessModal from '../../components/SecondaryMarketAccessModal/SecondaryMarketAccessModal';
import { TOOLTIP_TEXT } from '../../utils/constant';
import { computeSecondaryTradeTabState } from '../../utils/secondaryTradeTabState';
import useSecondaryMarketEligibility from '../../Hooks/useSecondaryMarketEligibility';

const TradeDrawer = (props) => {
  const { quickBuyPropertyData, isSingleTab, featchData, quickBuySecondaryOptions, isSell, isTradeFlow } = props;
  const [shareValue, setShareValue] = useState();
  const [sellPrice, setSellPrice] = useState();
  const [reviewData, setReviewData] = useState(BuyReviewData);
  const [successData, setSuccessData] = useState(BuySuccessData);
  const [, setBankModalOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [transferFundModal,setTransferFundModal]=useState(false);
  const [openBankModal, setOpenBankModal] = useState(false);

  const loading = useSelector((state) => state.secondaryPlaceBid.loading);
  const userProfileData = useSelector((state) => state.userData.userData);
  const isSellOrdersTradable = useSelector((state) => state.propertyDetails?.data?.data?.isSellOrdersTradable === true);
  const { isBuyEligible, buyIneligibleMsg, isSellEligible, sellIneligibleMsg, isEntityRestricted } = useSecondaryMarketEligibility();

  const phoneNumber = ['+1', formatePhoneNumber(userProfileData?.phoneNumber)];
  function handleSellClick() {
    setReviewData(SellReviewData);
    setSuccessData(SellSuccessData);
    props.modalState('StepThree');
  }
  const orderRef = useRef();
  const linkText = 'My Account>Account Balances';
  const linkPath = '/account/transactions';
  const openLink = () => {
    window.location.href = linkPath;
  };
  const {
    pin,
    loading: hookLoading,
    numberOfShare,
    isMaxRangeError,
    isMinRangeError,
    minButtonValue,
    sharesRemainingToPurchase,
    limitPrice,
    LowestAskPrice,
    highestBid,
    duration,
    totalInvestment,
    transactionFee,
    totalPurchase,
    isEditPayment,
    walletsData,
    selectedPaymentMethod,
    isTurmAndCondistion,
    partialFills,
    perSharePrice,
    userPurchasedShare,
    activeTab,
    setActiveTab,
    setPartialFills,
    setPin,
    resetPin,
    setResetPin,
    handleVerifyPin,
    handleForgotPin,
    setNumberOfShare,
    setLimitPrice,
    setDuration,
    handleSelectPaymentMethod,
    setIsTurmAndCondistion,
    handlePlaceBid,
    handleSellOrder,
    handleAddBank,
    setIsEditPayment,
    setLoading,
    isTOSModal,
    onTOSAgreementSuccess,
    onTOSModalClose,
    handleAfterAgreementAccepted,
    sharesRemainingToPurchaseForSecondary,
    propertyDetails,
  } = quickBuySecondaryOptions;

  const { sellTabDisabled, sellTabTooltip, buyTabDisabled, buyTabTooltip } = computeSecondaryTradeTabState({
    userPurchasedShare,
    isSellOrdersTradable,
    isSellEligible,
    sellIneligibleMsg,
    isTradeFlow,
    isBuyEligible,
    buyIneligibleMsg,
    isEntityRestricted,
  });

  const remainingOpenShares = Number(propertyDetails?.data?.userPurchaseDetail?.remainingOpenShares || 0);
  const isBuyOrdersPending = propertyDetails?.data?.userPurchaseDetail?.isBuyOrdersPending;
  const availableToSell = Math.max(0, userPurchasedShare - (isBuyOrdersPending ? 0 : remainingOpenShares));

  // Sync activeTab with isSell whenever the drawer opens (StepOne) or isSell changes.
  // Using props.open ensures we re-sync even if isSell was already '2' from a
  // previous sell click (isSell wouldn't change, so a [isSell]-only effect wouldn't fire).
  // This must stay tied to the clicked tab (isSell) — the PIN-verify eligibility
  // toast (useQuickBuySecondary) reads activeTab right after this to decide
  // whether to block, so it must reflect the button the user actually clicked,
  // not a pre-emptively swapped tab.
  useEffect(() => {
    if (props.open === 'StepOne') {
      setActiveTab(isSell);
    }
  }, [props.open, isSell]);

  // Once the tabs drawer itself is showing (StepTwo, i.e. eligibility has
  // already been resolved), fall back to the other tab if the one we landed
  // on turns out to be disabled — e.g. the Trade button always opens on Buy,
  // but Buy may be ineligible while Sell is not.
  useEffect(() => {
    if (props.open !== 'StepTwo') return;
    if (activeTab === '1' && buyTabDisabled && !sellTabDisabled) {
      setActiveTab('2');
    } else if (activeTab === '2' && sellTabDisabled && !buyTabDisabled) {
      setActiveTab('1');
    }
  }, [props.open, activeTab, buyTabDisabled, sellTabDisabled]);

  const openTransferModal = useCallback(() => {
    setTransferFundModal(true);
    props.modalState('');
  }, []);

  const addBankModalOpen = useMemo(() => openBankModal || secondaryBuy.ADD_BANK === props.open, [openBankModal, props.open]);
  return (
    <>
      <ResetPin phoneNumber={phoneNumber} mail={userProfileData?.email} setOpen={resetPin} setClose={() => setResetPin(false)} />
      {props.open === 'StepOne' && (
        <PinDrawerWithoutButton
          onCancel={() => {
            props.modalState();
            setPin('');
          }}
          open={props.open === 'StepOne'}
          pin={pin}
          loading={hookLoading}
          handleForgotPin={handleForgotPin}
          setPin={setPin}
          setLoading={setLoading}
          onSuccess={handleVerifyPin}
        />
      )}

      <BankAccountModal
        bankModalOpen={addBankModalOpen}
        setBankModalOpen={(value) => {
          if (value) {
            props.modalState(secondaryBuy.ADD_BANK);
          } else {
            props.modalState('');
          }
          setOpenBankModal(value);
        }}
        onSuccess={(value) => handleAddBank(value)}
        onClose={() => setIsEditPayment(false)}
      />
      <BankVerificationPendingModal
        open={props.open === secondaryBuy.VERIFY_BANK}
        onClose={() => props.modalState('')}
        onAddNewBank={() => props.modalState(secondaryBuy.ADD_BANK)}
      />
      <NoFundModal open={props.open === secondaryBuy.NO_MONEY} onClose={() => props.modalState('')} openTransferFunds={openTransferModal} />
      <PendingTransferModal
        open={props.open === secondaryBuy.PENDING_TRANSFER}
        onClose={() => props.modalState('')}
        openTransferFunds={openTransferModal}
      />
      <TransferMoreFunds open={transferFundModal} onOpen={openTransferModal} onClose={() => setTransferFundModal(false)} />
      
      {/* === Secondary Market Access Modal === */}
      <SecondaryMarketAccessModal
        visible={props.open === 'AccessModal' || props.open === 'AccessModal_Identity' || props.open === 'AccessModal_Agreement'}
        subStep={props.open}
        modalState={props.modalState}
        onRemindLater={() => props.modalState()}
        onAgreementSuccess={handleAfterAgreementAccepted}
        identificationDocs={userProfileData?.identificationDocs}
      />
      
      <Drawer
        className='buy-order footer-shadow quickbuy--drawer'
        width='100%'
        title={<p className='subhead-2 mb-0 ml-40 text-center'>Create order</p>}
        onClose={() => props.modalState()}
        open={props.open === 'StepTwo'}
        closeIcon={<CloseIcon />}
        footer={
          <>
            {activeTab === '1' && (
              <Tooltip title={buyTabTooltip}>
                <span>
                  <Button
                    type='primary'
                    className='w-100'
                    onClick={() => {
                      props.modalState('StepThree');
                      setReviewData(BuyReviewData);
                      props.modalState('StepThree');
                    }}
                    disabled={buyTabDisabled || !numberOfShare || !limitPrice || isMaxRangeError || Number(numberOfShare) % 1 !== 0 || Number(limitPrice) < 0.01 || Number(limitPrice) > 10000 || totalPurchase <= 0 || totalPurchase < 10 || totalPurchase > 10000}
                  >
                    Preview order
                  </Button>
                </span>
              </Tooltip>
            )}
            {activeTab === '2' && (
              <Tooltip title={sellTabTooltip}>
                <span>
                  <Button
                    disabled={
                      sellTabDisabled ||
                      !numberOfShare ||
                      !limitPrice ||
                      numberOfShare > userPurchasedShare ||
                      Number(numberOfShare) % 1 !== 0 ||
                      Number(limitPrice) < 0.01 ||
                      Number(limitPrice) > 10000 ||
                      totalPurchase <= 0 ||
                      totalPurchase < 10 ||
                      totalPurchase > 10000
                    }
                    type='primary'
                    className='w-100'
                    onClick={handleSellClick}
                  >
                    Preview order
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        }
      >
        <div className='buy-order-header'>
          <div className='header__img'>
            <LazyLoadImage
              onError={(element) => {
                element.target.src = Images.noPropertyFoundImage;
              }}
              src={quickBuyPropertyData?.images?.image1}
            />
          </div>
          <div className='header__content'>
            <p className='subhead-2 text-copper mb-0'>{quickBuyPropertyData?.propertyName || ''}</p>
            <div className='name-type mb-12'>
              <span className='fz-12'>{`${quickBuyPropertyData?.cityName || ''}, ${quickBuyPropertyData?.stateName || ''}`}</span>
              <span className='fz-12'>• </span>
              <span className='fz-12'>{quickBuyPropertyData?.propertyType || ''}</span>
            </div>
            <div className='share-details'>
              <div className='block'>
                <span className='text-taupe-80'>Last price</span>
                <span className='subhead-3'>{`$${getUsCurrencyFormatWithoutDecimal(quickBuyPropertyData?.lastTradedPrice || 0)}`}</span>
              </div>
              <div className='block'>
                <span className='text-taupe-80'>
                  All time
                  <Tooltip placement='bottom' title='All Time reflects the total change in share price since the initial offering'>
                    <span>
                      <Information className='cursor-pointer' verticalAlign='middle' marginLeft='4px' color='#1A1519' />
                    </span>
                  </Tooltip>
                </span>
                <span className='subhead-3'>{`${Number(quickBuyPropertyData?.allTime) > 0 ? '+' : ''} ${getUsCurrencyFormat(
                  quickBuyPropertyData?.allTime || 0
                )}%`}</span>
              </div>
              <div className='block'>
                <span className='text-taupe-80'>
                  Est. Dividend Yield
                  <Tooltip
                    placement='bottom'
                    title={TOOLTIP_TEXT.ESTIMATED_DIVIDEND_YIELD}
                  >
                    <span>
                      <Information className='cursor-pointer' verticalAlign='middle' marginLeft='4px' color='#1A1519' />
                    </span>
                  </Tooltip>
                </span>
                <span className='subhead-3'>{quickBuyPropertyData?.dividendYield ?? 'NA'}</span>
              </div>
            </div>
          </div>
        </div>
        {isSingleTab ? (
          <BuyOrder
            ref={orderRef}
            numberOfShare={numberOfShare}
            isMaxRangeError={isMaxRangeError}
            isMinRangeError={isMinRangeError}
            minButtonValue={minButtonValue}
            sharesRemainingToPurchase={sharesRemainingToPurchase}
            setNumberOfShare={setNumberOfShare}
            limitPrice={limitPrice}
            setLimitPrice={setLimitPrice}
            LowestAskPrice={LowestAskPrice}
            duration={duration}
            quickBuyPropertyData={quickBuyPropertyData}
            setDuration={setDuration}
            totalInvestment={totalInvestment}
            transactionFee={transactionFee}
            totalPurchase={getUsCurrencyFormat(totalPurchase)}
            rawTotalPurchase={totalPurchase}
            partialFills={partialFills}
            setPartialFills={setPartialFills}
            sharesRemainingToPurchaseForSecondary={sharesRemainingToPurchaseForSecondary}
            minimumPrice={propertyDetails?.data?.minimumPrice}
          />
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              if (key === '2') {
                setNumberOfShare(availableToSell.toString());
                setLimitPrice('');
                setDuration('Good Until Cancel');
                orderRef.current.resetFields();
              } else {
                setNumberOfShare(minButtonValue.toString());
                setLimitPrice('');
                setDuration('Good Until Cancel');
                orderRef.current.resetFields();
              }
            }}
            items={[
              {
                label: buyTabDisabled ? (
                  <Tooltip title={buyTabTooltip}>
                    <span className='trade-tab-label'>Buy</span>
                  </Tooltip>
                ) : <span className='trade-tab-label'>Buy</span>,
                key: '1',
                disabled: buyTabDisabled,
                children: (
                  <BuyOrder
                    ref={orderRef}
                    numberOfShare={numberOfShare}
                    isMaxRangeError={isMaxRangeError}
                    isMinRangeError={isMinRangeError}
                    minButtonValue={minButtonValue}
                    sharesRemainingToPurchase={sharesRemainingToPurchase}
                    setNumberOfShare={setNumberOfShare}
                    limitPrice={limitPrice}
                    setLimitPrice={setLimitPrice}
                    LowestAskPrice={LowestAskPrice}
                    duration={duration}
                    quickBuyPropertyData={quickBuyPropertyData}
                    setDuration={setDuration}
                    totalInvestment={totalInvestment}
                    transactionFee={transactionFee}
                    totalPurchase={getUsCurrencyFormat(totalPurchase)}
                    rawTotalPurchase={totalPurchase}
                    partialFills={partialFills}
                    setPartialFills={setPartialFills}
                    sharesRemainingToPurchaseForSecondary={sharesRemainingToPurchaseForSecondary}
                    minimumPrice={propertyDetails?.data?.minimumPrice}
                  />
                ),
              },
              {
                label: sellTabDisabled ? (
                  <Tooltip title={sellTabTooltip}>
                    <span className='trade-tab-label'>Sell</span>
                  </Tooltip>
                ) : <span className='trade-tab-label'>Sell</span>,
                key: '2',
                disabled: sellTabDisabled,
                children: (
                  <>
                    <SellOrder
                      ref={orderRef}
                      shareValue={shareValue}
                      setShareValue={setShareValue}
                      sellPrice={sellPrice}
                      setSellPrice={setSellPrice}
                      duration={duration}
                      setDuration={setDuration}
                      userPurchasedShare={userPurchasedShare}
                      remainingOpenShares={remainingOpenShares}
                      isBuyOrdersPending={isBuyOrdersPending}
                      numberOfShare={numberOfShare}
                      setNumberOfShare={setNumberOfShare}
                      limitPrice={limitPrice}
                      setLimitPrice={setLimitPrice}
                      highestBid={highestBid}
                      quickBuyPropertyData={quickBuyPropertyData}
                      totalInvestment={totalInvestment}
                      transactionFee={transactionFee}
                      totalPurchase={getUsCurrencyFormat(totalPurchase)}
                      partialFills={partialFills}
                      setPartialFills={setPartialFills}
                      minimumPrice={propertyDetails?.data?.minimumPrice}
                      rawTotalPurchase={totalPurchase}
                    />
                  </>
                ),
              },
            ]}
          />
        )}
      </Drawer>

      <Drawer
        className='review-buy-order footer-shadow quickbuy--drawer'
        width='100%'
        title={
          <>
            <ChevronLeft className='cursor-pointer' onClick={() => props.modalState('StepTwo')} />
            <span className='subhead-2 mb-0 ml-24 text-center title'>{reviewData?.title || ''}</span>
          </>
        }
        onClose={() => props.modalState()}
        open={props.open === 'StepThree'}
        closeIcon={<CloseIcon />}
        footer={
          <Button
            loading={loading}
            disabled={!isTurmAndCondistion}
            type='primary'
            className='w-100'
            onClick={() => (activeTab === '1' ? handlePlaceBid() : handleSellOrder())}
          >
            Place bid
          </Button>
        }
      >
        <p className='fw-600'>Investment details</p>
        <div className='d-flex flex-column g-16 fz-14'>
          <div className='d-flex align-items-center justify-content-space-between'>
            <span className='text-taupe-80'>Shares</span>
            <span>{numberOfShare || ''}</span>
          </div>
          <div className=' d-flex align-items-center justify-content-space-between'>
            <span className='text-taupe-80'>Price per share</span>
            <span>{`$${getUsCurrencyFormat(limitPrice || '')}`}</span>
          </div>
          <div className=' d-flex align-items-center justify-content-space-between'>
            <span className='text-taupe-80'>{activeTab === '2' ? 'Estimated Credit' : 'Total investment'}</span>
            <span>{`$${getUsCurrencyFormat(totalInvestment || '')}`}</span>
          </div>
          <div className=' d-flex align-items-center justify-content-space-between'>
            <span className='text-taupe-80'>Platform fee</span>
            <span>$2.50</span>
          </div>
          <div className=' d-flex align-items-center justify-content-space-between'>
            <span className='text-taupe-80'>Broker-Dealer/ATS Fee (1%)</span>
            <span>{`$${transactionFee || ''}`}</span>
          </div>
        </div>
        <hr className='mb-16 mt-16' />
        <div className='d-flex flex-column g-16 fz-14'>
          <div className='d-flex align-items-center justify-content-space-between'>
            <span className='text-taupe-80'>{activeTab === '2' ? 'Estimated Net Credit' : 'Total purchase (USD)'}</span>
            <span className='fw-600'>{`$${getUsCurrencyFormat(totalPurchase || '')}`}</span>
          </div>
          {/* Duration field commented out - Good Until Cancel / Good for the Day
          <div className='d-flex align-items-center justify-content-space-between'>
            <Tooltip
              title={
                <>
                  <p>
                    A "good until cancel" order is a buy or sell order that remains active until it is either executed or until the investor cancels
                    it.
                  </p>
                  <p className='mb-0'>
                    A "good for the day" order is a buy or sell order that is only good for the remainder of the trading day on which it is placed.
                    All "good for the day" orders will expire at 11:59 PM EST on the day they were placed.
                  </p>
                </>
              }
            >
              <span className='text-taupe-80 underline cursor-pointer'>Duration</span>
            </Tooltip>
            <span>{duration}</span>
          </div>
          */}
        </div>
        <p className='fw-600 mt-32'>Payment details</p>
        {!!isEditPayment ? (
          <FloatingLabel label='Payment method' inputId='bankSelect' inputValue={selectedPaymentMethod?.id} alwaysBig>
            <div id='bank-anchor' />
            <Form.Item className='mb-0' name='bankSelect'>
              <Select
                id='bankSelect'
                onChange={(value) => {
                  value !== -1 ? handleSelectPaymentMethod(value) : setBankModalOpen(true);
                }}
                defaultValue={selectedPaymentMethod?.id}
                value={selectedPaymentMethod?.id}
                menuItemSelectedIcon={<CheckCircle verticalAlign='-8px' />}
                getPopupContainer={() => document.getElementById('bank-anchor')}
                onDropdownVisibleChange={() => setBankOpen(!bankOpen)}
                suffixIcon={bankOpen ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
              >
                {walletsData?.map((item) => (
                  <Select.Option value={item.id}>
                    {getBankLogo(item?.brandName)} {item?.brandName === 'DWOLLA' ? 'Realbricks Wallet' : item?.brandName || ''}{' '}
                    {item?.sourceType !== 'Wallet' && item?.sourceType !== 'add new' && `*** ${item.lastFourDigit || ''}`}
                    {item?.sourceType === 'Wallet' && '$'}
                    {item?.sourceType === 'Wallet' ? (
                      totalPurchase > item?.availableBalance ? (
                        <span className='error-text'>Insufficient balance</span>
                      ) : (
                        <span>{`${
                          getUsCurrencyFormat(item?.availableBalance) || ''
                        }`}</span>
                      )
                    ) : (
                      <span>{`${
                        getUsCurrencyFormat(item?.availableBalance) || ''
                      }`}</span>
                    )}
                  </Select.Option>
                ))}
              </Select>
              {selectedPaymentMethod?.sourceType === 'Wallet' && totalPurchase > selectedPaymentMethod?.availableBalance && (
                <div className='pt-10 error-text'>
                  <span style={{ cursor: 'pointer' }} onClick={openLink}>
                    Please go to {<a href={linkPath}>{linkText}</a>} to transfer funds into your account.
                  </span>
                </div>
              )}
            </Form.Item>
          </FloatingLabel>
        ) : (
          <>
            <div className='d-flex g-8 align-items-center'>
              {getBankLogo(selectedPaymentMethod?.brandName)}
              <span className='fz-16'>
                {selectedPaymentMethod?.brandName === 'DWOLLA' ? 'Realbricks Wallet' : selectedPaymentMethod?.brandName || ''}
              </span>
              {selectedPaymentMethod?.sourceType !== 'Wallet' && (
                <span className='text-taupe-80 fz-16'>{`*** ${selectedPaymentMethod?.lastFourDigit || ''}`}</span>
              )}
              {selectedPaymentMethod?.sourceType === 'Wallet' &&
              selectedPaymentMethod?.availableBalance !== undefined &&
              selectedPaymentMethod?.availableBalance !== null ? (
                totalPurchase > selectedPaymentMethod?.availableBalance ? (
                  <span className='text-taupe-80 fz-16 error-text'>Insufficient balance</span>
                ) : (
                  <span className='text-taupe-80 fz-16'>{`$${getUsCurrencyFormat(selectedPaymentMethod?.availableBalance)}`}</span>
                )
              ) : null}
            </div>
            {selectedPaymentMethod?.sourceType === 'Wallet' && totalPurchase > selectedPaymentMethod?.availableBalance && (
              <div className='pt-10 error-text'>
                <span style={{ cursor: 'pointer' }} onClick={openLink}>
                  Please go to {<a href={linkPath}>{linkText}</a>} to transfer funds into your account.
                </span>
              </div>
            )}
          </>
        )}
        <div className='d-flex mt-48'>
          <Checkbox checked={isTurmAndCondistion} onChange={(value) => setIsTurmAndCondistion(value.target.checked)} className='mr-16' />
          <span className='cursor-pointer fz-14'>
            <span onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}>I have read and accept the </span>
            <Link to={{ pathname: '/legal', hash: 'Terms of use' }} className='underline' target='_blank'>
              Terms of Use
            </Link>
            <span onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}>, the </span>
            <Link to={{ pathname: '/legal', hash: 'Other disclosure here' }} className='underline' target='_blank'>
              Regulation A Investment Disclosure
            </Link>
            <span onClick={() => setIsTurmAndCondistion(!isTurmAndCondistion)}>, and </span>
            <Link to={{ pathname: '/legal', hash: 'Privacy Policy' }} className='underline' target='_blank'>
              Privacy Policy
            </Link>
            .
          </span>
        </div>
      </Drawer>

      <Drawer
        className='success footer-shadow quickbuy--drawer'
        width='100%'
        title={false}
        onClose={() => featchData()}
        open={props.open === 'success'}
        closable={false}
        footer={false}
      >
        <div className='mb-16 success__vector'>
          <img src={Images.success} alt='success vector art' />
        </div>
        <h4 className='success__title fz-24'>{activeTab === '2' ? SellSuccessData?.title : successData?.title || ''}</h4>
        <p className='mb-48 text-taupe fz-16'>
          {activeTab === '2'
            ? `You have successfully placed a sell order for ${numberOfShare || ''} shares ($${getUsCurrencyFormat(perSharePrice || 0)} each) of ${
                quickBuyPropertyData?.propertyName
              }. You will be notified through email about any progress made toward your order.`
            : `You have successfully requested to buy ${numberOfShare} shares ($${getUsCurrencyFormat(perSharePrice || 0)} each) of ${quickBuyPropertyData?.propertyName}. You will be notified through email about any progress made toward your buy order.`}
        </p>
        <Button
          type='primary'
          className='w-100'
          onClick={() => {
            featchData();
          }}
        >
          Done
        </Button>
      </Drawer>

      {isTOSModal && <ListingAgreement isOpen={isTOSModal} onClose={onTOSModalClose} onAgreementAccept={onTOSAgreementSuccess} />}
    </>
  );
};

export default TradeDrawer;
